
import { supabase } from "@/integrations/supabase/client";

/**
 * Sends a notification to a service vendor about events related to their service
 * @param eventType Type of event (booking, review)
 * @param serviceId ID of the service
 * @param data Additional data about the event
 * @returns Promise<boolean> Success status of the notification
 */
export const sendVendorNotification = async (
  eventType: 'booking' | 'review' | 'cancellation',
  serviceId: number,
  data: Record<string, any>
): Promise<boolean> => {
  try {
    console.log(`=== VENDOR NOTIFICATION START ===`);
    console.log(`Event Type: ${eventType}`);
    console.log(`Service ID: ${serviceId}`);
    console.log(`Data:`, data);
    
    // Check if the service has notifications enabled for this event type
    const { data: service, error: serviceError } = await supabase
      .from('services')
      .select('owner_id, booking_notifications, review_notifications, name')
      .eq('id', serviceId)
      .single();
      
    if (serviceError || !service) {
      console.error('Error fetching service:', serviceError);
      return false;
    }
    
    console.log('Service notification settings:', {
      serviceId,
      serviceName: service.name,
      bookingNotifications: service.booking_notifications,
      reviewNotifications: service.review_notifications,
      ownerId: service.owner_id
    });
    
    // Check if service has an owner
    if (!service.owner_id) {
      console.log(`Service ${serviceId} has no owner, skipping notification`);
      return false;
    }
    
    // Check if notifications are enabled for this event type
    if (eventType === 'booking' && !service.booking_notifications) {
      console.log(`Booking notifications are disabled for service ${serviceId}`);
      return false;
    }
    
    if (eventType === 'review' && !service.review_notifications) {
      console.log(`Review notifications are disabled for service ${serviceId}`);
      return false;
    }
    
    if (eventType === 'cancellation' && !service.booking_notifications) {
      console.log(`Cancellation notifications are disabled for service ${serviceId}`);
      return false;
    }
    
    // Fetch the owner's profile to get their email
    const { data: ownerProfile, error: ownerError } = await supabase
      .from('profiles')
      .select('email, display_name')
      .eq('id', service.owner_id)
      .single();
      
    if (ownerError || !ownerProfile) {
      console.error('Error fetching owner profile:', ownerError);
      return false;
    }
    
    console.log('Owner profile:', {
      ownerId: service.owner_id,
      email: ownerProfile.email,
      displayName: ownerProfile.display_name
    });
    
    // Fetch notification preferences (they should exist due to auto-creation)
    console.log('=== FETCHING NOTIFICATION PREFERENCES ===');
    const { data: preferences, error: preferencesError } = await supabase
      .from('notification_preferences')
      .select('*')
      .eq('user_id', service.owner_id)
      .maybeSingle();
    
    console.log('Notification preferences query result:', {
      error: preferencesError,
      preferences: preferences
    });
    
    // If no preferences exist, create default ones (fallback safety net)
    let finalPreferences = preferences;
    if (!preferences) {
      console.log('=== NO PREFERENCES FOUND - CREATING DEFAULTS ===');
      
      const defaultPreferences = {
        user_id: service.owner_id,
        notification_email: ownerProfile.email,
        notification_phone: null,
        email_new_bookings: true,
        email_booking_updates: true,
        email_reviews: true,
        sms_new_bookings: false,
        sms_booking_updates: false,
        sms_reviews: false
      };
      
      // Try to create default preferences
      const { data: createdPrefs, error: createError } = await supabase
        .from('notification_preferences')
        .insert(defaultPreferences)
        .select()
        .single();
        
      if (createError) {
        console.error('Error creating default preferences:', createError);
        finalPreferences = defaultPreferences;
      } else {
        console.log('Created default preferences:', createdPrefs);
        finalPreferences = createdPrefs;
      }
    }
    
    // Determine which notification types should be enabled based on event type and preferences
    let emailEnabled = false;
    let smsEnabled = false;
    
    console.log('=== NOTIFICATION TYPE DETERMINATION ===');
    console.log('Event type:', eventType);
    
    if (eventType === 'booking') {
      emailEnabled = finalPreferences?.email_new_bookings === true;
      smsEnabled = finalPreferences?.sms_new_bookings === true;
      console.log('Booking event - checking email_new_bookings and sms_new_bookings');
    } else if (eventType === 'review') {
      emailEnabled = finalPreferences?.email_reviews === true;
      smsEnabled = finalPreferences?.sms_reviews === true;
      console.log('Review event - checking email_reviews and sms_reviews');
    }
    
    console.log('Final notification settings:', {
      emailEnabled,
      smsEnabled,
      vendorPhone: finalPreferences?.notification_phone
    });
    
    // Enhanced data for the notification
    const enhancedData = {
      ...data,
      serviceName: service.name,
      vendorEmail: ownerProfile.email,
      vendorName: ownerProfile.display_name,
      vendorPhone: finalPreferences?.notification_phone,
      emailEnabled,
      smsEnabled,
      preferencesAutoCreated: !preferences
    };
    
    console.log('=== CALLING VENDOR-NOTIFICATIONS EDGE FUNCTION ===');
    
    // Call the vendor-notifications edge function to deliver the notification
    const { data: result, error } = await supabase.functions.invoke(
      'vendor-notifications',
      {
        body: {
          eventType,
          serviceId,
          data: enhancedData,
          ownerEmail: ownerProfile.email,
          ownerName: ownerProfile.display_name
        }
      }
    );
    
    console.log('=== EDGE FUNCTION RESPONSE ===');
    if (error) {
      console.error('Error sending vendor notification:', error);
      return false;
    }
    
    console.log('Vendor notification result:', {
      result,
      success: result?.success,
      message: result?.message,
      emailSent: result?.emailSent,
      smsSent: result?.smsSent
    });
    
    console.log(`=== VENDOR NOTIFICATION END: ${result?.success ? 'SUCCESS' : 'FAILED'} ===`);
    return result?.success || false;
  } catch (error: any) {
    console.error('=== VENDOR NOTIFICATION EXCEPTION ===');
    console.error('Exception in sendVendorNotification:', error);
    return false;
  }
};
